import {
  IBenefitWorklist,
  IComparisonChecksPage,
  IComparisonChecksRequest,
  IComparisonClusterDetail,
  IComparisonFilters,
  IComparisonSummary,
  IEngineExplanation,
  IEngineExplanationKey,
  IRecordVerdictRequest,
  IRescoreClustersRequest,
  IRescoreClustersRun,
  IRescoreRequest,
  IRescoreResult,
  IVerificationTrend,
  IVerifiedFilters,
  IVerifiedStatus,
  IWorklistClusterKey,
  IWorklistFilters,
} from "types/BenefitComparison/BenefitComparison";
import {
  ILineAnalysis,
  ILineAnalysisRequest,
} from "types/BenefitComparison/LineAnalysis";

import { api } from ".";

const toParams = (filter: object) =>
  Object.fromEntries(
    Object.entries(filter).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

/**
 * CLE-2820 — the Engine worklist. Read-only but for two writes, SuperAdmin on the API.
 *
 * Every read carries verdict data, so all of them are tagged `BenefitVerdict` and both mutations
 * invalidate the tag — after a verdict or a re-run the scorecard, the worklist and the checks all
 * have to move together.
 *
 * 🚨 THE TREND IS NOT INVALIDATED BY THE RE-RUN, and that is deliberate: the re-run writes no
 * snapshot, so its line cannot have moved. Re-fetching it would only redraw the same points.
 */
const benefitComparisonQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * CLE-2820 follow-up — THE WORKLIST: one ranked row per cluster, merged and sorted server-side.
     * 🚨 All-time. There is no date window on this screen.
     */
    getBenefitWorklist: builder.query<
      { data: IBenefitWorklist; message: string },
      IWorklistFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/worklist",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    /** CLE-2820 follow-up — one point per NIGHTLY run. Not filtered: a snapshot is the whole set. */
    getBenefitVerificationTrend: builder.query<
      { data: IVerificationTrend; message: string },
      { runs?: number }
    >({
      query: (request) => ({
        url: "benefitComparison/trend",
        params: toParams(request),
      }),
    }),
    getBenefitComparisonSummary: builder.query<
      { data: IComparisonSummary; message: string },
      IComparisonFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/summary",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitComparisonChecks: builder.query<
      { data: IComparisonChecksPage; message: string },
      IComparisonChecksRequest
    >({
      query: (request) => ({
        url: "benefitComparison/checks",
        params: toParams(request),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    getBenefitComparisonCluster: builder.query<
      { data: IComparisonClusterDetail; message: string },
      IComparisonChecksRequest & { examplePverifyResponseId?: string }
    >({
      query: (request) => ({
        url: "benefitComparison/cluster",
        params: toParams(request),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    /**
     * "Analyze line items" — re-runs today's engine on ONE check with recording on and explains
     * every response line. On demand only (a lazy query); read-only; no AI.
     */
    getBenefitLineAnalysis: builder.query<
      { data: ILineAnalysis; message: string },
      ILineAnalysisRequest
    >({
      query: (request) => ({
        url: "benefitComparison/lineAnalysis",
        params: toParams(request),
      }),
    }),
    /**
     * CLE-2821 — what today's engine makes of every verified check. Derived by the nightly run and
     * by the on-demand re-score, so both writes invalidate it.
     */
    getBenefitVerifiedStatus: builder.query<
      { data: IVerifiedStatus; message: string },
      IVerifiedFilters
    >({
      query: (filters) => ({
        url: "benefitComparison/verified",
        params: toParams(filters),
      }),
      providesTags: ["BenefitVerdict"],
    }),
    // 🚨 The reviewer is NOT in the body — the API takes it from the authenticated user.
    recordBenefitVerdict: builder.mutation<
      { data: { id: string }; message: string },
      IRecordVerdictRequest
    >({
      query: (body) => ({
        url: "benefitComparison/verdict",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    /**
     * CLE-2820 follow-up — re-run today's engine over stored responses. Synchronous; seconds.
     *
     * 🚨 It calls pVerify for nothing, runs no new eligibility check, re-asks the AI nothing,
     * recomputes no legacy pick and changes no estimate that has already been sent.
     */
    rescoreBenefitChecks: builder.mutation<
      { data: IRescoreResult; message: string },
      IRescoreRequest
    >({
      query: (body) => ({
        url: "benefitComparison/rescore",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    /**
     * "Rerun all clusters" — the same re-run over every cluster the filtered worklist shows.
     *
     * 🚨 202 AT ONCE: the API replays and rebuilds in the BACKGROUND (thousands of checks would pass
     * the endpoint timeout), and the worklist read's `freshness.rescoring` says so until it is done.
     * 409 while a run is already in flight. Same contract as the per-cluster re-run: stored responses
     * only, no pVerify, no AI, no legacy, no effect on an estimate already sent.
     */
    rescoreBenefitClusters: builder.mutation<
      { data: IRescoreClustersRun; message: string },
      IRescoreClustersRequest
    >({
      query: (body) => ({
        url: "benefitComparison/rescore/clusters",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    /**
     * Mark a whole worklist cluster "engine correct", or undo it. Re-marking a cluster that is
     * already marked replaces the old mark with one carrying today's engine range.
     *
     * 🚨 NO VERDICT IS WRITTEN AND NO SCORE MOVES — only which worklist rows are listed. The tag is
     * shared with the worklist so the row disappears (or comes back) at once.
     */
    dismissWorklistCluster: builder.mutation<
      { data: { id: string }; message: string },
      IWorklistClusterKey & { note?: string }
    >({
      query: (body) => ({
        url: "benefitComparison/worklist/dismiss",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    undismissWorklistCluster: builder.mutation<
      { data: { revoked: number }; message: string },
      IWorklistClusterKey
    >({
      query: (body) => ({
        url: "benefitComparison/worklist/undismiss",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    /**
     * Flag a whole worklist cluster "needs more review", or unflag it. Re-flagging replaces the old
     * flag (and its note) with a new one.
     *
     * 🚨 NO VERDICT IS WRITTEN AND NO SCORE MOVES — the row stays listed and only its border and the
     * "Flagged" filter change. Shares the tag with the worklist so the border appears at once.
     */
    flagWorklistCluster: builder.mutation<
      { data: { id: string }; message: string },
      IWorklistClusterKey & { note?: string }
    >({
      query: (body) => ({
        url: "benefitComparison/worklist/flag",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    unflagWorklistCluster: builder.mutation<
      { data: { revoked: number }; message: string },
      IWorklistClusterKey
    >({
      query: (body) => ({
        url: "benefitComparison/worklist/unflag",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BenefitVerdict"],
    }),
    /**
     * "Ask Cyrus to Explain" — the newest explanation for one check + benefit (any status), or null.
     * Not tagged: it is polled while Cyrus works, and nothing else on the page changes it.
     */
    getLatestEngineExplanation: builder.query<
      { data: IEngineExplanation | null; message: string },
      IEngineExplanationKey
    >({
      query: (key) => ({
        url: "benefitComparison/explanation/latest",
        params: toParams(key),
      }),
    }),
    /** One explanation by id — what the panel polls after asking. */
    getEngineExplanation: builder.query<
      { data: IEngineExplanation; message: string },
      string
    >({
      query: (id) => ({ url: `benefitComparison/explanation/${id}` }),
    }),
    /**
     * Queue an explanation for the agents app's worker. Returns the row: a new QUEUED one, the one
     * already in flight for this check + benefit, or (without `rerun`) a recent finished one for the
     * same engine configuration. A `question` (the "Ask Cyrus" dialog's optional text) always
     * queues a new run unless one is already in flight — `message: "reused"` then says the
     * question was not sent. 🚨 The requester is NOT in the body.
     */
    requestEngineExplanation: builder.mutation<
      { data: IEngineExplanation; message: string },
      IEngineExplanationKey & { rerun?: boolean; question?: string }
    >({
      query: (body) => ({
        url: "benefitComparison/explanation",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetBenefitWorklistQuery,
  useGetBenefitVerificationTrendQuery,
  useGetBenefitComparisonSummaryQuery,
  useGetBenefitComparisonChecksQuery,
  useGetBenefitComparisonClusterQuery,
  useLazyGetBenefitLineAnalysisQuery,
  useGetBenefitVerifiedStatusQuery,
  useRecordBenefitVerdictMutation,
  useRescoreBenefitChecksMutation,
  useRescoreBenefitClustersMutation,
  useDismissWorklistClusterMutation,
  useUndismissWorklistClusterMutation,
  useFlagWorklistClusterMutation,
  useUnflagWorklistClusterMutation,
  useGetLatestEngineExplanationQuery,
  useGetEngineExplanationQuery,
  useRequestEngineExplanationMutation,
} = benefitComparisonQueries;
